import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AGENCY_PROJECT_KIND_MAP,
  PROJECT_HEALTH_MAP,
  type AgencyProjectView,
  type PortfolioView,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, PageHead } from '../../components/ui';
import { Avatar, useTeamDirectory } from '../../components/People';
import { useAuth } from '../../auth/AuthProvider';
import { NewProjectModal } from './Projects';

/**
 * صفحةُ المحفظة — مشاريعُ خطِّ عملٍ واحدٍ في مكانٍ واحد.
 *
 * تُفتح بالنقر على محفظةٍ في صفحة المحافظ. تقرأ حالةَ الخطّ كلِّه في رأسها
 * (كم مشروعًا، كم أُنجز، كم متأخّرٌ وفي خطر) ثمّ تعرض مشاريعَه ببطاقاتها
 * نفسِها التي في صفحة المشاريع. ومن هنا يُنشأ مشروعٌ جديدٌ داخل المحفظة
 * مباشرةً — محفظتُه مثبَّتةٌ لا تُختار.
 */
export function AgencyPortfolioDetail() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { atLeast } = useAuth();
  const { byId } = useTeamDirectory();
  const [creating, setCreating] = useState(false);

  const portfolios = useQuery({
    queryKey: ['agency', 'portfolios'],
    queryFn: () => api<PortfolioView[]>('/agency/portfolios'),
  });
  const projects = useQuery({
    queryKey: ['agency', 'projects', false],
    queryFn: () => api<AgencyProjectView[]>('/agency/projects'),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['agency'] });

  const pf = (portfolios.data ?? []).find((p) => p.id === id);
  const mine = (projects.data ?? []).filter((p) => p.portfolioId === id && !p.archived);
  const canEdit = atLeast('MANAGER');
  const pct = pf?.taskCount ? Math.round((pf.doneCount / pf.taskCount) * 100) : 0;

  if (portfolios.isError) {
    return (
      <div className="page">
        <ErrorState error={portfolios.error} onRetry={() => void portfolios.refetch()} />
      </div>
    );
  }

  if (portfolios.isPending) {
    return (
      <div className="page">
        <div className="skeleton" style={{ height: 200 }} />
      </div>
    );
  }

  if (!pf) {
    return (
      <div className="page">
        <EmptyState
          title="المحفظة غير موجودة"
          text="ربّما حُذفت أو نُقل رابطُها."
          action={
            <Link className="btn" to="/agency/portfolios">
              العودة إلى المحافظ
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="page">
      <Link to="/agency/portfolios" className="btn btn-sm mb-3">
        <Icon name="chevronRight" size={14} />
        المحافظ
      </Link>

      <PageHead
        title={pf.name}
        subtitle={pf.description || 'خطُّ عملٍ يُقرأ ويُقارَن — مشاريعه في مكانٍ واحد'}
        actions={
          canEdit ? (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={15} />
              مشروع جديد
            </button>
          ) : undefined
        }
      />

      <div className="pf-stats mb-4">
        <span className="pf-stat">
          <b className="num">{pf.projectCount}</b>
          <span className="tiny mute">من المشاريع</span>
        </span>
        <span className="pf-stat">
          <b className="num">{pct}٪</b>
          <span className="tiny mute">أُنجز</span>
        </span>
        <span className={`pf-stat${pf.overdueCount > 0 ? ' is-late' : ''}`}>
          <b className="num">{pf.overdueCount}</b>
          <span className="tiny mute">متأخّرة</span>
        </span>
        <span className={`pf-stat${pf.atRisk + pf.offTrack > 0 ? ' is-warn' : ''}`}>
          <b className="num">{pf.atRisk + pf.offTrack}</b>
          <span className="tiny mute">في خطر</span>
        </span>
      </div>

      <div className="ag-progress mb-4" title={`${pf.doneCount}/${pf.taskCount}`}>
        <span className="ag-progress-fill" style={{ width: `${pct}%`, background: pf.color }} />
      </div>

      {mine.length === 0 ? (
        <EmptyState
          title="لا مشاريع في هذه المحفظة بعد"
          text="أنشئ أوّلَ مشروعٍ داخلها — محفظتُه مثبَّتةٌ عليها تلقائيًّا."
          action={
            canEdit ? (
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                أنشئ مشروعًا هنا
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="ag-projects">
          {mine.map((p) => {
            const ppct = p.taskCount ? Math.round((p.doneCount / p.taskCount) * 100) : 0;
            return (
              <Link
                key={p.id}
                to={`/agency/projects/${p.id}`}
                className={`ag-project-card${p.overdueCount > 0 ? ' is-late' : ''}`}
              >
                <span className="ag-project-bar" style={{ background: p.color }} />
                <div className="ag-project-body">
                  <div className="ag-project-top">
                    {p.accountName ? (
                      <Avatar name={p.accountName} src={p.accountAvatarUrl} size={38} />
                    ) : (
                      <span
                        className="ag-project-int"
                        style={{ background: `${p.color}1a`, color: p.color }}
                      >
                        <Icon name="briefcase" size={17} />
                      </span>
                    )}
                    <div className="ag-project-id">
                      <strong className="ag-project-name">{p.name}</strong>
                      <span className="tiny mute">
                        {p.accountName ?? 'مشروع داخليّ'}
                        {p.contractNumber ? ` · ${p.contractNumber}` : ''}
                      </span>
                    </div>
                    <Badge kind={AGENCY_PROJECT_KIND_MAP[p.kind]?.kind}>
                      {AGENCY_PROJECT_KIND_MAP[p.kind]?.label ?? p.kind}
                    </Badge>
                  </div>

                  {p.description && <p className="tiny mute ag-clamp">{p.description}</p>}

                  <div className="ag-progress" title={`${p.doneCount}/${p.taskCount}`}>
                    <span
                      className="ag-progress-fill"
                      style={{ width: `${ppct}%`, background: p.color }}
                    />
                  </div>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="tiny mute num">
                      {p.doneCount}/{p.taskCount} · {ppct}٪
                    </span>
                    <span className="row" style={{ gap: 6, alignItems: 'center' }}>
                      {p.health && (
                        <Badge kind={PROJECT_HEALTH_MAP[p.health]?.kind}>
                          {PROJECT_HEALTH_MAP[p.health]?.label}
                        </Badge>
                      )}
                      {p.overdueCount > 0 && (
                        <span className="ag-chip ag-chip-late num">{p.overdueCount} متأخّرة</span>
                      )}
                      {p.overdueCount === 0 && p.nextDueAt && (
                        <span className="tiny mute num">القادم {date(p.nextDueAt)}</span>
                      )}
                      {p.ownerId && byId.get(p.ownerId) && (
                        <span title={`المالك: ${byId.get(p.ownerId)!.name}`}>
                          <Avatar
                            name={byId.get(p.ownerId)!.name}
                            src={byId.get(p.ownerId)!.avatarUrl}
                            size={22}
                          />
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {creating && (
        <NewProjectModal
          presetPortfolioId={pf.id}
          presetPortfolioName={pf.name}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}
